import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { FileUpload } from '@/components/documents/FileUpload';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatFileSize } from '@/services/documentApi';
import * as taxFilingsApi from '@/services/taxFilingsApi';
import {
  DECLARATION_KIND,
  DRAFT_COPY_STATUS,
  DRAFT_COPY_STATUS_LABELS,
  DraftCopyStatus,
  TAX_FILINGS_TAB,
  TaxFilingDocument,
  TaxFilingsTab,
} from '@/types/taxFilings';
import { Download, Eye, FileText, ThumbsDown, ThumbsUp, Upload } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const VALID_TABS = new Set<string>(Object.values(TAX_FILINGS_TAB));

function normalizeTab(tab?: string): TaxFilingsTab {
  if (tab && VALID_TABS.has(tab)) return tab as TaxFilingsTab;
  return TAX_FILINGS_TAB.TAX_SUMMARY;
}

function draftBadgeVariant(
  status?: DraftCopyStatus
): 'success' | 'destructive' | 'secondary' | 'warning' {
  if (status === DRAFT_COPY_STATUS.CLIENT_VERIFIED) return 'success';
  if (status === DRAFT_COPY_STATUS.CLIENT_REJECTED) return 'destructive';
  if (status === DRAFT_COPY_STATUS.PENDING_CLIENT) return 'warning';
  return 'secondary';
}

export interface TaxFilingsPanelProps {
  mode: 'user' | 'admin';
  taxYear: number;
  userId?: string;
  tenantId?: string | null;
  defaultTab?: string;
  showTitle?: boolean;
  onTabChange?: (tab: TaxFilingsTab) => void;
}

type UploadKind =
  | 'tax-summary'
  | 'draft-copy'
  | 'declaration-unsigned'
  | 'declaration-signed'
  | 'final-copy';

export function TaxFilingsPanel({
  mode,
  taxYear,
  userId,
  tenantId,
  defaultTab,
  showTitle = false,
  onTabChange,
}: TaxFilingsPanelProps) {
  const isAdmin = mode === 'admin';
  const [activeTab, setActiveTab] = useState<TaxFilingsTab>(() => normalizeTab(defaultTab));

  const [taxSummaries, setTaxSummaries] = useState<TaxFilingDocument[]>([]);
  const [draftCopies, setDraftCopies] = useState<TaxFilingDocument[]>([]);
  const [unsignedDeclarations, setUnsignedDeclarations] = useState<TaxFilingDocument[]>([]);
  const [signedDeclarations, setSignedDeclarations] = useState<TaxFilingDocument[]>([]);
  const [finalCopies, setFinalCopies] = useState<TaxFilingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [uploadKind, setUploadKind] = useState<UploadKind | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [acceptDoc, setAcceptDoc] = useState<TaxFilingDocument | null>(null);
  const [rejectDoc, setRejectDoc] = useState<TaxFilingDocument | null>(null);
  const [statusReviewTab, setStatusReviewTab] = useState<TaxFilingsTab | null>(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  useEffect(() => {
    setActiveTab(normalizeTab(defaultTab));
  }, [defaultTab]);

  const adminOptions = { taxYear, tenantId };

  const loadTaxSummaries = useCallback(async () => {
    if (isAdmin) {
      if (!userId) return;
      setTaxSummaries(await taxFilingsApi.listTaxSummariesForUser(userId, adminOptions));
    } else {
      setTaxSummaries(await taxFilingsApi.listMyTaxSummaries(taxYear));
    }
  }, [isAdmin, userId, taxYear, tenantId]);

  const loadDraftCopies = useCallback(async () => {
    if (isAdmin) {
      if (!userId) return;
      setDraftCopies(await taxFilingsApi.listDraftCopiesForUser(userId, adminOptions));
    } else {
      setDraftCopies(await taxFilingsApi.listMyDraftCopies(taxYear));
    }
  }, [isAdmin, userId, taxYear, tenantId]);

  const loadDeclarations = useCallback(async () => {
    if (isAdmin) {
      if (!userId) return;
      const [unsigned, signed] = await Promise.all([
        taxFilingsApi.listDeclarationsForUser(userId, {
          ...adminOptions,
          kind: DECLARATION_KIND.UNSIGNED,
        }),
        taxFilingsApi.listDeclarationsForUser(userId, {
          ...adminOptions,
          kind: DECLARATION_KIND.SIGNED,
        }),
      ]);
      setUnsignedDeclarations(unsigned);
      setSignedDeclarations(signed);
    } else {
      const [unsigned, signed] = await Promise.all([
        taxFilingsApi.listMyDeclarations(taxYear, DECLARATION_KIND.UNSIGNED),
        taxFilingsApi.listMyDeclarations(taxYear, DECLARATION_KIND.SIGNED),
      ]);
      setUnsignedDeclarations(unsigned);
      setSignedDeclarations(signed);
    }
  }, [isAdmin, userId, taxYear, tenantId]);

  const loadFinalCopies = useCallback(async () => {
    if (isAdmin) {
      if (!userId) return;
      setFinalCopies(await taxFilingsApi.listFinalCopiesForUser(userId, adminOptions));
    } else {
      setFinalCopies(await taxFilingsApi.listMyFinalCopies(taxYear));
    }
  }, [isAdmin, userId, taxYear, tenantId]);

  const loadActiveTab = useCallback(async () => {
    setIsLoading(true);
    try {
      switch (activeTab) {
        case TAX_FILINGS_TAB.TAX_SUMMARY:
          await loadTaxSummaries();
          break;
        case TAX_FILINGS_TAB.DRAFT_COPIES:
          await loadDraftCopies();
          break;
        case TAX_FILINGS_TAB.DECLARATION:
          await loadDeclarations();
          break;
        case TAX_FILINGS_TAB.FINAL_COPIES:
          await loadFinalCopies();
          break;
      }
    } catch {
      toast.error('Failed to load tax filings');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, loadTaxSummaries, loadDraftCopies, loadDeclarations, loadFinalCopies]);

  useEffect(() => {
    if (isAdmin && !userId) return;
    loadActiveTab();
  }, [isAdmin, userId, loadActiveTab]);

  const getPreviewUrl = async (doc: TaxFilingDocument, tab: TaxFilingsTab): Promise<string> => {
    if (isAdmin) {
      if (!userId) throw new Error('Missing userId');
      switch (tab) {
        case TAX_FILINGS_TAB.TAX_SUMMARY:
          return taxFilingsApi.getTaxSummaryPreviewAdmin(userId, doc.id, tenantId);
        case TAX_FILINGS_TAB.DRAFT_COPIES:
          return taxFilingsApi.getDraftCopyPreviewAdmin(userId, doc.id, tenantId);
        case TAX_FILINGS_TAB.DECLARATION:
          return taxFilingsApi.getDeclarationPreviewAdmin(userId, doc.id, tenantId);
        case TAX_FILINGS_TAB.FINAL_COPIES:
          return taxFilingsApi.getFinalCopyPreviewAdmin(userId, doc.id, tenantId);
      }
    }
    switch (tab) {
      case TAX_FILINGS_TAB.TAX_SUMMARY:
        return taxFilingsApi.getMyTaxSummaryPreview(doc.id);
      case TAX_FILINGS_TAB.DRAFT_COPIES:
        return taxFilingsApi.getMyDraftCopyPreview(doc.id);
      case TAX_FILINGS_TAB.DECLARATION:
        return taxFilingsApi.getMyDeclarationPreview(doc.id);
      case TAX_FILINGS_TAB.FINAL_COPIES:
        return taxFilingsApi.getMyFinalCopyPreview(doc.id);
    }
  };

  const handleView = async (doc: TaxFilingDocument, tab: TaxFilingsTab) => {
    try {
      const url = await getPreviewUrl(doc, tab);
      if (url) window.open(url, '_blank');
      else toast.error('Preview not available');
    } catch {
      toast.error('Failed to open preview');
    }
  };

  const handleDownload = async (doc: TaxFilingDocument, tab: TaxFilingsTab) => {
    try {
      const url = await getPreviewUrl(doc, tab);
      if (!url) {
        toast.error('Download not available');
        return;
      }
      await taxFilingsApi.downloadFromPreviewUrl(url, doc.name);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const handleUpload = async (files: File[]) => {
    if (!uploadKind || files.length === 0) return;
    if (isAdmin && !userId) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        switch (uploadKind) {
          case 'tax-summary':
            await taxFilingsApi.createTaxSummaryForUser(userId!, file, adminOptions);
            break;
          case 'draft-copy':
            await taxFilingsApi.createDraftCopyForUser(userId!, file, adminOptions);
            break;
          case 'declaration-unsigned':
            await taxFilingsApi.createDeclarationForUser(
              userId!,
              file,
              DECLARATION_KIND.UNSIGNED,
              adminOptions
            );
            break;
          case 'declaration-signed':
            if (isAdmin) {
              await taxFilingsApi.createDeclarationForUser(
                userId!,
                file,
                DECLARATION_KIND.SIGNED,
                adminOptions
              );
            } else {
              await taxFilingsApi.uploadMySignedDeclaration(file, taxYear);
            }
            break;
          case 'final-copy':
            await taxFilingsApi.createFinalCopyForUser(userId!, file, adminOptions);
            break;
        }
      }

      const countLabel = files.length === 1 ? '1 file' : `${files.length} files`;
      switch (uploadKind) {
        case 'tax-summary':
          toast.success(`Tax summary uploaded (${countLabel})`);
          await loadTaxSummaries();
          break;
        case 'draft-copy':
          toast.success(`Draft copy uploaded (${countLabel})`);
          await loadDraftCopies();
          break;
        case 'declaration-unsigned':
          toast.success(`Unsigned declaration uploaded (${countLabel})`);
          await loadDeclarations();
          break;
        case 'declaration-signed':
          toast.success(`Signed declaration uploaded (${countLabel})`);
          await loadDeclarations();
          break;
        case 'final-copy':
          toast.success(`Final copy uploaded (${countLabel})`);
          await loadFinalCopies();
          break;
      }
      setUploadKind(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const updateReviewStatus = async (
    doc: TaxFilingDocument,
    tab: TaxFilingsTab,
    status: DraftCopyStatus,
    comment?: string
  ) => {
    setIsSubmittingStatus(true);
    try {
      const payload = {
        status,
        ...(comment !== undefined && comment !== '' ? { rejectionComment: comment } : {}),
      };
      if (isAdmin) {
        if (!userId) return;
        if (tab === TAX_FILINGS_TAB.TAX_SUMMARY) {
          await taxFilingsApi.updateTaxSummaryStatusForUser(userId, doc.id, payload, tenantId);
        } else if (tab === TAX_FILINGS_TAB.FINAL_COPIES) {
          await taxFilingsApi.updateFinalCopyStatusForUser(userId, doc.id, payload, tenantId);
        } else {
          await taxFilingsApi.updateDraftCopyStatusForUser(userId, doc.id, payload, tenantId);
        }
      } else if (tab === TAX_FILINGS_TAB.TAX_SUMMARY) {
        await taxFilingsApi.updateMyTaxSummaryStatus(doc.id, payload);
      } else if (tab === TAX_FILINGS_TAB.FINAL_COPIES) {
        await taxFilingsApi.updateMyFinalCopyStatus(doc.id, payload);
      } else {
        await taxFilingsApi.updateMyDraftCopyStatus(doc.id, payload);
      }

      const label =
        tab === TAX_FILINGS_TAB.TAX_SUMMARY
          ? 'Tax summary'
          : tab === TAX_FILINGS_TAB.FINAL_COPIES
            ? 'Final copy'
            : 'Draft copy';
      toast.success(
        status === DRAFT_COPY_STATUS.CLIENT_VERIFIED
          ? `${label} accepted`
          : `${label} rejected`
      );
      setAcceptDoc(null);
      setRejectDoc(null);
      setStatusReviewTab(null);
      setRejectionComment('');
      if (tab === TAX_FILINGS_TAB.TAX_SUMMARY) await loadTaxSummaries();
      else if (tab === TAX_FILINGS_TAB.FINAL_COPIES) await loadFinalCopies();
      else await loadDraftCopies();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const renderDocActions = (doc: TaxFilingDocument, tab: TaxFilingsTab) => (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Button variant="outline" size="sm" onClick={() => handleView(doc, tab)}>
        <Eye className="mr-1 h-4 w-4" />
        View
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleDownload(doc, tab)}>
        <Download className="mr-1 h-4 w-4" />
        Download
      </Button>
    </div>
  );

  const renderDocMeta = (doc: TaxFilingDocument) => (
    <p className="text-sm text-muted-foreground mt-0.5">
      {formatFileSize(doc.size)}
      {doc.createdAt && <> · {new Date(doc.createdAt).toLocaleDateString()}</>}
    </p>
  );

  const renderDocList = (
    docs: TaxFilingDocument[],
    tab: TaxFilingsTab,
    emptyTitle: string,
    emptyDescription: string,
    options?: {
      showReviewStatus?: boolean;
      emptyAction?: { label: string; onClick: () => void };
    }
  ) => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      );
    }

    if (docs.length === 0) {
      return (
        <EmptyState
          icon={FileText}
          title={emptyTitle}
          description={emptyDescription}
          action={options?.emptyAction}
        />
      );
    }

    return (
      <div className="space-y-3">
        {docs.map((doc) => (
          <Card key={doc.id}>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{doc.name}</p>
                  {renderDocMeta(doc)}
                  {options?.showReviewStatus && doc.status && (
                    <>
                      <Badge variant={draftBadgeVariant(doc.status)} className="mt-2">
                        {DRAFT_COPY_STATUS_LABELS[doc.status] ?? doc.status}
                      </Badge>
                      {doc.status === DRAFT_COPY_STATUS.CLIENT_REJECTED && doc.rejectionComment && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Rejection reason: {doc.rejectionComment}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {renderDocActions(doc, tab)}
                  {options?.showReviewStatus &&
                    doc.status === DRAFT_COPY_STATUS.PENDING_CLIENT && (
                      <>
                        <Button
                          size="sm"
                          className="gap-1"
                          onClick={() => {
                            setStatusReviewTab(tab);
                            setAcceptDoc(doc);
                          }}
                        >
                          <ThumbsUp className="h-4 w-4" />
                          Accept
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="gap-1"
                          onClick={() => {
                            setRejectionComment('');
                            setStatusReviewTab(tab);
                            setRejectDoc(doc);
                          }}
                        >
                          <ThumbsDown className="h-4 w-4" />
                          Reject
                        </Button>
                      </>
                    )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const uploadDialogTitle = (() => {
    switch (uploadKind) {
      case 'tax-summary':
        return 'Upload tax summary';
      case 'draft-copy':
        return 'Upload draft copy';
      case 'declaration-unsigned':
        return 'Upload unsigned declaration';
      case 'declaration-signed':
        return 'Upload signed declaration';
      case 'final-copy':
        return 'Upload final copy';
      default:
        return 'Upload file';
    }
  })();

  return (
    <div className="space-y-6">
      {showTitle && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tax Filings</h1>
          <p className="text-muted-foreground mt-1">
            Tax summary, draft copies, declarations, and final copies for {taxYear}.
          </p>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          const next = normalizeTab(v);
          setActiveTab(next);
          onTabChange?.(next);
        }}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1">
          <TabsTrigger value={TAX_FILINGS_TAB.TAX_SUMMARY}>Tax Summary</TabsTrigger>
          <TabsTrigger value={TAX_FILINGS_TAB.DRAFT_COPIES}>Draft Copies</TabsTrigger>
          <TabsTrigger value={TAX_FILINGS_TAB.DECLARATION}>Declaration</TabsTrigger>
          <TabsTrigger value={TAX_FILINGS_TAB.FINAL_COPIES}>Final Copies</TabsTrigger>
        </TabsList>

        <TabsContent value={TAX_FILINGS_TAB.TAX_SUMMARY} className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUploadKind('tax-summary')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload tax summary
              </Button>
            </div>
          )}
          {renderDocList(
            taxSummaries,
            TAX_FILINGS_TAB.TAX_SUMMARY,
            'No tax summary',
            isAdmin
              ? `Upload a tax summary for ${taxYear}.`
              : `No tax summary has been uploaded for ${taxYear} yet.`,
            {
              showReviewStatus: true,
              ...(isAdmin
                ? {
                    emptyAction: {
                      label: 'Upload tax summary',
                      onClick: () => setUploadKind('tax-summary'),
                    },
                  }
                : {}),
            }
          )}
        </TabsContent>

        <TabsContent value={TAX_FILINGS_TAB.DRAFT_COPIES} className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUploadKind('draft-copy')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload draft copy
              </Button>
            </div>
          )}
          {renderDocList(
            draftCopies,
            TAX_FILINGS_TAB.DRAFT_COPIES,
            'No draft copies',
            isAdmin
              ? `Upload a draft copy for ${taxYear} for this user to review.`
              : `You don't have any draft copies for ${taxYear} yet.`,
            {
              showReviewStatus: true,
              ...(isAdmin
                ? {
                    emptyAction: {
                      label: 'Upload draft copy',
                      onClick: () => setUploadKind('draft-copy'),
                    },
                  }
                : {}),
            }
          )}
        </TabsContent>

        <TabsContent value={TAX_FILINGS_TAB.DECLARATION} className="space-y-8">
          <section className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Unsigned</h2>
                <p className="text-sm text-muted-foreground">
                  Declaration forms awaiting signature
                </p>
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUploadKind('declaration-unsigned')}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload unsigned
                </Button>
              )}
            </div>
            {renderDocList(
              unsignedDeclarations,
              TAX_FILINGS_TAB.DECLARATION,
              'No unsigned declarations',
              isAdmin
                ? `Upload an unsigned declaration for ${taxYear}.`
                : `No unsigned declarations for ${taxYear}.`
            )}
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Signed</h2>
                <p className="text-sm text-muted-foreground">
                  Signed declaration forms
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUploadKind('declaration-signed')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload signed
              </Button>
            </div>
            {renderDocList(
              signedDeclarations,
              TAX_FILINGS_TAB.DECLARATION,
              'No signed declarations',
              `No signed declarations for ${taxYear}.`,
              {
                emptyAction: {
                  label: 'Upload signed',
                  onClick: () => setUploadKind('declaration-signed'),
                },
              }
            )}
          </section>
        </TabsContent>

        <TabsContent value={TAX_FILINGS_TAB.FINAL_COPIES} className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUploadKind('final-copy')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload final copy
              </Button>
            </div>
          )}
          {renderDocList(
            finalCopies,
            TAX_FILINGS_TAB.FINAL_COPIES,
            'No final copies',
            isAdmin
              ? `Upload a final copy for ${taxYear}.`
              : `No final copies have been uploaded for ${taxYear} yet.`,
            {
              showReviewStatus: true,
              ...(isAdmin
                ? {
                    emptyAction: {
                      label: 'Upload final copy',
                      onClick: () => setUploadKind('final-copy'),
                    },
                  }
                : {}),
            }
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!uploadKind}
        onOpenChange={(open) => {
          if (!open) setUploadKind(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{uploadDialogTitle}</DialogTitle>
            <DialogDescription>
              {uploadKind === 'tax-summary'
                ? 'Select one or more files to upload (any file type).'
                : 'Select one or more files to upload.'}
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            onFilesSelected={() => {}}
            onUpload={handleUpload}
            maxFiles={60}
            minFiles={1}
            accept={uploadKind === 'tax-summary' ? null : undefined}
            isUploading={isUploading}
            uploadProgress={[]}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!acceptDoc}
        onOpenChange={(open) => {
          if (!open) {
            setAcceptDoc(null);
            setStatusReviewTab(null);
          }
        }}
        title="Accept document"
        description={
          acceptDoc
            ? `Are you sure you want to accept "${acceptDoc.name}"?`
            : ''
        }
        confirmLabel="Accept"
        onConfirm={() => {
          if (acceptDoc && statusReviewTab) {
            void updateReviewStatus(
              acceptDoc,
              statusReviewTab,
              DRAFT_COPY_STATUS.CLIENT_VERIFIED
            );
          }
        }}
        loading={isSubmittingStatus}
      />

      <Dialog
        open={!!rejectDoc}
        onOpenChange={(open) => {
          if (!open) {
            setRejectDoc(null);
            setStatusReviewTab(null);
            setRejectionComment('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
            <DialogDescription>
              {rejectDoc
                ? `Reject "${rejectDoc.name}". You can optionally add a comment.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rejection-comment">Comment (optional)</Label>
            <textarea
              id="rejection-comment"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              placeholder="Reason for rejection"
              disabled={isSubmittingStatus}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDoc(null);
                setRejectionComment('');
              }}
              disabled={isSubmittingStatus}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isSubmittingStatus}
              onClick={() => {
                if (rejectDoc && statusReviewTab) {
                  void updateReviewStatus(
                    rejectDoc,
                    statusReviewTab,
                    DRAFT_COPY_STATUS.CLIENT_REJECTED,
                    rejectionComment.trim()
                  );
                }
              }}
            >
              {isSubmittingStatus ? 'Processing...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TaxFilingsPanel;
